import { useState, useEffect, useCallback } from 'react';
import { Mail, Send, Inbox, ArrowLeft, RefreshCw, LogOut, ChevronLeft, ChevronRight, X, Paperclip, Download, FileText, Image, File, User, FolderOpen, Settings, Check, Upload, Package, Video } from 'lucide-react';
import ProductsTab from './admin/ProductsTab';

const API = process.env.REACT_APP_BACKEND_URL;
const PER_PAGE = 10;

const AdminPage = () => {
  const [token, setToken] = useState(localStorage.getItem('admin_token') || '');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [logging, setLogging] = useState(false);

  if (!token) {
    return (
      <LoginScreen
        password={password}
        setPassword={setPassword}
        error={loginError}
        loading={logging}
        onLogin={async () => {
          setLogging(true);
          setLoginError('');
          try {
            const res = await fetch(`${API}/api/admin/login`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ password }),
            });
            if (!res.ok) throw new Error('Falsches Passwort');
            const data = await res.json();
            localStorage.setItem('admin_token', data.token);
            setToken(data.token);
          } catch (e) {
            setLoginError(e.message);
          } finally {
            setLogging(false);
          }
        }}
      />
    );
  }

  return (
    <AdminDashboard
      token={token}
      onLogout={() => {
        localStorage.removeItem('admin_token');
        setToken('');
      }}
    />
  );
};

/* ─── Login ─── */
const LoginScreen = ({ password, setPassword, error, loading, onLogin }) => (
  <div className="min-h-screen flex items-center justify-center" style={{ background: '#050e1c' }}>
    <div className="w-full max-w-sm mx-4">
      <div className="text-center mb-8">
        <div className="w-14 h-14 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mx-auto mb-4">
          <Mail className="w-7 h-7 text-emerald-400" />
        </div>
        <h1 className="font-['Space_Grotesk'] text-2xl font-bold text-white">Admin Panel</h1>
        <p className="text-gray-500 text-sm mt-1">LAIzyones Verwaltung</p>
      </div>
      <form
        onSubmit={(e) => { e.preventDefault(); onLogin(); }}
        className="rounded-2xl border border-white/10 p-6"
        style={{ background: 'rgba(255,255,255,0.03)' }}
      >
        <label className="block text-sm text-gray-400 mb-2">Passwort</label>
        <input
          data-testid="admin-password-input"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-600 focus:border-emerald-500/50 focus:outline-none transition-colors text-sm"
          placeholder="Admin-Passwort eingeben"
          autoFocus
        />
        {error && <p className="text-red-400 text-xs mt-2">{error}</p>}
        <button
          data-testid="admin-login-button"
          type="submit"
          disabled={loading || !password}
          className="w-full mt-4 px-4 py-3 rounded-xl bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 text-white text-sm font-semibold transition-colors"
        >
          {loading ? 'Anmelden...' : 'Anmelden'}
        </button>
      </form>
    </div>
  </div>
);

/* ─── Folders config ─── */
const FOLDERS = [
  { key: 'inbox', label: 'Posteingang', icon: Inbox, countKey: 'inbox' },
  { key: 'info', label: 'Info', icon: Mail, countKey: 'info' },
  { key: 'david', label: 'David', icon: User, countKey: 'david' },
  { key: 'philipp', label: 'Philipp', icon: User, countKey: 'philipp' },
];

/* ─── Main Admin Dashboard ─── */
const AdminDashboard = ({ token, onLogout }) => {
  const [mainTab, setMainTab] = useState('email'); // email | products
  const [tab, setTab] = useState('inbox');
  const [folder, setFolder] = useState('inbox');
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [composing, setComposing] = useState(false);
  const [replying, setReplying] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalEmails, setTotalEmails] = useState(0);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [dragOverFolder, setDragOverFolder] = useState(null);
  const [emailDetail, setEmailDetail] = useState(null);
  const [showSignatureEditor, setShowSignatureEditor] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchUnread = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/admin/emails/unread-counts`, { headers });
      if (res.ok) setUnreadCounts(await res.json());
    } catch {}
  }, [token]);

  const fetchEmails = useCallback(async () => {
    setLoading(true);
    try {
      const endpoint = tab === 'inbox' ? 'inbox' : 'sent';
      const params = new URLSearchParams({ page: String(page), limit: String(PER_PAGE) });
      if (tab === 'inbox') params.set('folder', folder);
      const res = await fetch(`${API}/api/admin/emails/${endpoint}?${params}`, { headers });
      if (res.status === 401) { onLogout(); return; }
      const data = await res.json();
      setEmails(data.emails || []);
      setTotalPages(data.pages || 1);
      setTotalEmails(data.total || 0);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [tab, folder, page, token]);

  useEffect(() => { fetchEmails(); }, [fetchEmails]);
  useEffect(() => { if (tab === 'inbox') fetchUnread(); }, [tab, fetchUnread]);

  const selectEmail = async (emailItem) => {
    // Fetch full email detail from IMAP
    try {
      const params = new URLSearchParams({ folder: emailItem.folder || folder, uid: emailItem.uid });
      const res = await fetch(`${API}/api/admin/emails/detail?${params}`, { headers });
      if (res.ok) {
        const detail = await res.json();
        setEmailDetail(detail);
        setSelected(emailItem);
        // Mark as read
        if (!emailItem.read) {
          await fetch(`${API}/api/admin/emails/${emailItem.uid}/read?folder=${emailItem.folder || folder}`, { method: 'PUT', headers });
          setEmails(prev => prev.map(e => e.uid === emailItem.uid ? { ...e, read: true } : e));
          fetchUnread();
        }
      }
    } catch (e) {
      console.error(e);
    }
  };

  const toggleRead = async (e, emailItem) => {
    e.stopPropagation();
    try {
      const res = await fetch(`${API}/api/admin/emails/${emailItem.uid}/toggle-read?folder=${emailItem.folder || folder}`, { method: 'PUT', headers });
      if (res.ok) {
        const data = await res.json();
        setEmails(prev => prev.map(em => em.uid === emailItem.uid ? { ...em, read: data.read } : em));
        fetchUnread();
      }
    } catch {}
  };

  const moveToFolder = async (uid, fromFolder, toFolder) => {
    try {
      await fetch(`${API}/api/admin/emails/${uid}/folder`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ from_folder: fromFolder, to_folder: toFolder }),
      });
      // Remove from current list since it moved
      setEmails(prev => prev.filter(e => e.uid !== uid));
      setSelected(null);
      setEmailDetail(null);
      fetchUnread();
    } catch {}
  };

  const handleFolderDrop = (e, folderKey) => {
    e.preventDefault();
    setDragOverFolder(null);
    const data = e.dataTransfer.getData('text/plain');
    if (!data) return;
    try {
      const { uid, fromFolder } = JSON.parse(data);
      if (uid && folderKey && folderKey !== fromFolder) {
        moveToFolder(uid, fromFolder, folderKey);
      }
    } catch {}
  };

  const switchTab = (newTab) => {
    setTab(newTab);
    setSelected(null);
    setEmailDetail(null);
    setPage(1);
    if (newTab === 'inbox') setFolder('inbox');
  };

  const switchFolder = (f) => {
    setFolder(f);
    setSelected(null);
    setEmailDetail(null);
    setPage(1);
  };

  const currentFolderLabel = FOLDERS.find(f => f.key === folder)?.label || 'Posteingang';

  return (
    <div className="min-h-screen flex" style={{ background: '#050e1c' }}>
      {/* Sidebar */}
      <div className="w-64 flex-shrink-0 border-r border-white/5 flex flex-col" style={{ background: 'rgba(255,255,255,0.02)' }}>
        <div className="p-4 border-b border-white/5">
          <div className="flex items-center gap-2 mb-3">
            <img src="/logo-white.png" alt="LAIzyones" className="h-7 object-contain" />
            <span className="font-['Space_Grotesk'] font-bold text-white text-sm">Admin</span>
          </div>
          {/* Top-Level Tabs */}
          <div className="flex rounded-lg bg-white/5 p-0.5">
            <button
              data-testid="main-tab-email"
              onClick={() => setMainTab('email')}
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium transition-all ${
                mainTab === 'email' ? 'bg-emerald-500/20 text-emerald-400 shadow-sm' : 'text-gray-500 hover:text-gray-300'
              }`}
            >
              <Mail className="w-3.5 h-3.5" /> E-Mails
            </button>
            <button
              data-testid="main-tab-products"
              onClick={() => setMainTab('products')}
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium transition-all ${
                mainTab === 'products' ? 'bg-emerald-500/20 text-emerald-400 shadow-sm' : 'text-gray-500 hover:text-gray-300'
              }`}
            >
              <Package className="w-3.5 h-3.5" /> Produkte
            </button>
          </div>
        </div>

        {mainTab === 'email' && (
          <>
            <nav className="flex-1 p-2 space-y-0.5 overflow-y-auto">
          <button
            data-testid="nav-inbox"
            onClick={() => switchTab('inbox')}
            className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm transition-colors ${
              tab === 'inbox' ? 'bg-emerald-500/10 text-emerald-400' : 'text-gray-400 hover:bg-white/5'
            }`}
          >
            <span className="flex items-center gap-2.5"><Inbox className="w-4 h-4" /> Posteingang</span>
            {(unreadCounts.total || 0) > 0 && (
              <span className="min-w-[20px] h-5 px-1.5 flex items-center justify-center rounded-full bg-emerald-500 text-white text-xs font-bold">
                {unreadCounts.total}
              </span>
            )}
          </button>
          <button
            data-testid="nav-sent"
            onClick={() => switchTab('sent')}
            className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm transition-colors ${
              tab === 'sent' ? 'bg-emerald-500/10 text-emerald-400' : 'text-gray-400 hover:bg-white/5'
            }`}
          >
            <Send className="w-4 h-4" /> Gesendet
          </button>

          {tab === 'inbox' && (
            <>
              <div className="pt-3 pb-1 px-3">
                <span className="text-[10px] font-bold uppercase tracking-widest text-gray-600">Ordner</span>
              </div>
              {FOLDERS.map((f) => {
                const Icon = f.icon;
                const count = unreadCounts[f.countKey] || 0;
                const isDropTarget = f.key !== 'inbox';
                return (
                  <button
                    key={f.key}
                    data-testid={`folder-${f.key}`}
                    onClick={() => switchFolder(f.key)}
                    onDragOver={isDropTarget ? (e) => { e.preventDefault(); setDragOverFolder(f.key); } : undefined}
                    onDragLeave={isDropTarget ? () => setDragOverFolder(null) : undefined}
                    onDrop={isDropTarget ? (e) => handleFolderDrop(e, f.key) : undefined}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-all ${
                      dragOverFolder === f.key
                        ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40 scale-[1.02]'
                        : folder === f.key ? 'bg-white/8 text-white border border-transparent' : 'text-gray-500 hover:bg-white/5 hover:text-gray-300 border border-transparent'
                    }`}
                  >
                    <span className="flex items-center gap-2.5"><Icon className="w-3.5 h-3.5" /> {f.label}</span>
                    {count > 0 && (
                      <span className="min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-emerald-500/20 text-emerald-400 text-[10px] font-bold">
                        {count}
                      </span>
                    )}
                  </button>
                );
              })}
            </>
          )}
        </nav>

        <div className="p-2 border-t border-white/5 space-y-1">
          <button
            data-testid="compose-button"
            onClick={() => { setComposing(true); setReplying(null); setSelected(null); setEmailDetail(null); setShowSignatureEditor(false); }}
            className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-white text-sm font-medium transition-colors"
          >
            <Send className="w-4 h-4" /> Neue E-Mail
          </button>
          <button
            data-testid="signature-button"
            onClick={() => { setShowSignatureEditor(true); setComposing(false); setReplying(null); setSelected(null); setEmailDetail(null); }}
            className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
              showSignatureEditor ? 'bg-emerald-500/10 text-emerald-400' : 'text-gray-500 hover:text-gray-300 hover:bg-white/5'
            }`}
          >
            <Settings className="w-3.5 h-3.5" /> Signatur
          </button>
          <button
            onClick={onLogout}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-gray-500 hover:text-gray-300 text-xs transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" /> Abmelden
          </button>
        </div>
          </>
        )}

        {mainTab === 'products' && (
          <>
            <div className="flex-1" />
            <div className="p-2 border-t border-white/5">
              <button
                onClick={onLogout}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-gray-500 hover:text-gray-300 text-xs transition-colors"
              >
                <LogOut className="w-3.5 h-3.5" /> Abmelden
              </button>
            </div>
          </>
        )}
      </div>

      {/* Main Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {mainTab === 'products' ? (
          <ProductsTab token={token} onLogout={onLogout} />
        ) : (
        <>
        <div className="h-14 flex items-center justify-between px-4 border-b border-white/5" style={{ background: 'rgba(255,255,255,0.02)' }}>
          <div className="flex items-center gap-3">
            {selected && (
              <button onClick={() => { setSelected(null); setEmailDetail(null); }} className="text-gray-500 hover:text-white transition-colors">
                <ChevronLeft className="w-5 h-5" />
              </button>
            )}
            <h2 className="font-['Space_Grotesk'] font-semibold text-white text-sm">
              {showSignatureEditor ? 'E-Mail-Signatur' : composing ? 'Neue E-Mail' : replying ? 'Antworten' : selected ? (emailDetail?.subject || selected.subject) : tab === 'sent' ? 'Gesendet' : currentFolderLabel}
            </h2>
            {!selected && !composing && !replying && (
              <span className="text-gray-600 text-xs">{totalEmails} E-Mail{totalEmails !== 1 ? 's' : ''}</span>
            )}
          </div>
          <button onClick={() => { fetchEmails(); fetchUnread(); }} className="text-gray-500 hover:text-emerald-400 transition-colors" title="Aktualisieren">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        <div className="flex-1 overflow-auto">
          {showSignatureEditor ? (
            <SignatureEditor token={token} onLogout={onLogout} />
          ) : (composing || replying) ? (
            <ComposeView
              token={token}
              replyTo={replying}
              onClose={() => { setComposing(false); setReplying(null); }}
              onSent={() => { setComposing(false); setReplying(null); setTab('sent'); setPage(1); fetchEmails(); }}
              onLogout={onLogout}
            />
          ) : selected && emailDetail ? (
            <EmailDetail
              email={emailDetail}
              currentFolder={folder}
              onReply={() => setReplying(emailDetail)}
              onBack={() => { setSelected(null); setEmailDetail(null); }}
              token={token}
              onMoveToFolder={(uid, fromFolder, toFolder) => moveToFolder(uid, fromFolder, toFolder)}
            />
          ) : (
            <>
              <EmailList
                emails={emails}
                loading={loading}
                onSelect={selectEmail}
                tab={tab}
                currentFolder={folder}
                onToggleRead={toggleRead}
              />
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-3 py-4 border-t border-white/5">
                  <button
                    data-testid="page-prev"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium text-gray-400 hover:text-white hover:bg-white/5 disabled:opacity-30 disabled:pointer-events-none transition-colors"
                  >
                    <ChevronLeft className="w-3.5 h-3.5" /> Zurück
                  </button>
                  <span className="text-gray-500 text-xs">Seite {page} von {totalPages}</span>
                  <button
                    data-testid="page-next"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium text-gray-400 hover:text-white hover:bg-white/5 disabled:opacity-30 disabled:pointer-events-none transition-colors"
                  >
                    Weiter <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
        </>
        )}
      </div>
    </div>
  );
};

/* ─── Email List ─── */
const EmailList = ({ emails, loading, onSelect, tab, currentFolder, onToggleRead }) => {
  if (loading) return <div className="p-8 text-center text-gray-600 text-sm">Lade E-Mails...</div>;
  if (!emails.length) return <div className="p-8 text-center text-gray-600 text-sm">Keine E-Mails vorhanden</div>;

  return (
    <div className="divide-y divide-white/5">
      {emails.map((eml, i) => {
        const isUnread = eml.read === false;
        return (
          <div
            key={eml.uid || i}
            data-testid={`email-item-${i}`}
            draggable={tab === 'inbox'}
            onDragStart={(e) => {
              e.dataTransfer.setData('text/plain', JSON.stringify({ uid: eml.uid, fromFolder: eml.folder || currentFolder }));
              e.dataTransfer.effectAllowed = 'move';
            }}
            onClick={() => onSelect(eml)}
            className={`w-full text-left px-4 py-3.5 transition-colors group cursor-pointer flex items-start gap-3 ${
              isUnread ? 'bg-emerald-500/5 hover:bg-emerald-500/8' : 'hover:bg-white/3'
            } ${tab === 'inbox' ? 'cursor-grab active:cursor-grabbing' : ''}`}
          >
            {tab === 'inbox' && (
              <button
                data-testid={`toggle-read-${i}`}
                onClick={(e) => onToggleRead(e, eml)}
                title={isUnread ? 'Als gelesen markieren' : 'Als ungelesen markieren'}
                className="mt-1 flex-shrink-0 group/dot"
              >
                <span className={`block w-3 h-3 rounded-full border-2 transition-colors ${
                  isUnread
                    ? 'bg-emerald-400 border-emerald-400 group-hover/dot:bg-emerald-300'
                    : 'bg-transparent border-gray-600 group-hover/dot:border-emerald-400'
                }`} />
              </button>
            )}
            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <p className={`text-sm truncate ${isUnread ? 'text-white font-bold' : 'text-white font-medium'}`}>
                      {tab === 'inbox' ? eml.from : (eml.to || '')}
                    </p>
                    {isUnread && (
                      <span data-testid={`neu-badge-${i}`} className="flex-shrink-0 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                        NEU
                      </span>
                    )}
                  </div>
                  <p className={`text-sm truncate mt-0.5 ${isUnread ? 'text-gray-200' : 'text-gray-300'}`}>
                    {eml.subject || '(Kein Betreff)'}
                  </p>
                </div>
                <span className="text-gray-600 text-xs flex-shrink-0 mt-0.5">
                  {eml.timestamp ? new Date(eml.timestamp).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}
                </span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

/* ─── Helpers ─── */
const fileIcon = (filename) => {
  const ext = (filename || '').split('.').pop().toLowerCase();
  if (['jpg','jpeg','png','gif','webp','svg'].includes(ext)) return <Image className="w-4 h-4" />;
  if (['pdf','doc','docx','txt','csv','xls','xlsx'].includes(ext)) return <FileText className="w-4 h-4" />;
  return <File className="w-4 h-4" />;
};

const formatSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/* ─── Email Detail ─── */
const EmailDetail = ({ email, currentFolder, onReply, onBack, token, onMoveToFolder }) => {
  const downloadAttachment = (att) => {
    const params = new URLSearchParams({
      folder: email.folder || currentFolder,
      uid: email.uid,
      part: String(att.part_index),
      authorization: `Bearer ${token}`,
    });
    window.open(`${API}/api/admin/emails/attachment?${params}`, '_blank');
  };

  const isSent = email.folder === 'sent';

  return (
    <div className="p-6">
      <div className="mb-6 space-y-2">
        <div className="flex items-center gap-2 text-sm">
          <span className="text-gray-500 w-12">Von:</span>
          <span className="text-white">{email.from}</span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span className="text-gray-500 w-12">An:</span>
          <span className="text-white">{email.to}</span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span className="text-gray-500 w-12">Betreff:</span>
          <span className="text-white font-medium">{email.subject || '(Kein Betreff)'}</span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span className="text-gray-500 w-12">Datum:</span>
          <span className="text-gray-400">
            {email.timestamp ? new Date(email.timestamp).toLocaleString('de-DE') : ''}
          </span>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {!isSent && (
          <button
            data-testid="reply-button"
            onClick={onReply}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm hover:bg-emerald-500/20 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Antworten
          </button>
        )}
        {!isSent && (
          <div className="flex items-center gap-1">
            <span className="text-gray-600 text-xs mr-1"><FolderOpen className="w-3.5 h-3.5 inline" /> Verschieben:</span>
            {['info', 'david', 'philipp'].filter(f => f !== (email.folder || currentFolder)).map(f => (
              <button
                key={f}
                data-testid={`move-to-${f}`}
                onClick={() => onMoveToFolder(email.uid, email.folder || currentFolder, f)}
                className="px-2.5 py-1 rounded text-xs font-medium transition-colors bg-white/5 text-gray-500 hover:text-white hover:bg-white/10 border border-white/5"
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
            {(email.folder || currentFolder) !== 'inbox' && (
              <button
                data-testid="move-to-inbox"
                onClick={() => onMoveToFolder(email.uid, email.folder || currentFolder, 'inbox')}
                className="px-2.5 py-1 rounded text-xs font-medium transition-colors bg-white/5 text-gray-500 hover:text-white hover:bg-white/10 border border-white/5"
              >
                Posteingang
              </button>
            )}
          </div>
        )}
      </div>

      {email.attachments && email.attachments.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center gap-2 text-sm text-gray-400 mb-2">
            <Paperclip className="w-4 h-4" />
            <span>{email.attachments.length} Anhang{email.attachments.length > 1 ? 'e' : ''}</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {email.attachments.map((att, i) => (
              <button
                key={i}
                onClick={() => downloadAttachment(att)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 border border-white/10 hover:bg-white/8 hover:border-emerald-500/30 transition-colors group"
              >
                <span className="text-gray-500 group-hover:text-emerald-400 transition-colors">
                  {fileIcon(att.filename)}
                </span>
                <span className="text-white text-xs truncate max-w-[180px]">{att.filename}</span>
                {att.size > 0 && <span className="text-gray-600 text-xs">{formatSize(att.size)}</span>}
                <Download className="w-3.5 h-3.5 text-gray-600 group-hover:text-emerald-400 transition-colors" />
              </button>
            ))}
          </div>
        </div>
      )}

      <div
        className="rounded-xl border border-white/5 p-5"
        style={{ background: 'rgba(255,255,255,0.02)' }}
      >
        {email.html ? (
          <div
            className="prose prose-invert prose-sm max-w-none [&_a]:text-emerald-400"
            style={{ color: '#e5e7eb' }}
            dangerouslySetInnerHTML={{
              __html: email.html
                .replace(/color\s*:\s*#000[0-9a-fA-F]*/gi, 'color:#e5e7eb')
                .replace(/color\s*:\s*black/gi, 'color:#e5e7eb')
                .replace(/color\s*:\s*rgb\(\s*0\s*,\s*0\s*,\s*0\s*\)/gi, 'color:#e5e7eb')
            }}
          />
        ) : (
          <pre className="text-gray-200 text-sm whitespace-pre-wrap font-sans">{email.text || '(Kein Inhalt)'}</pre>
        )}
      </div>
    </div>
  );
};

/* ─── Compose View ─── */
const ComposeView = ({ token, replyTo, onClose, onSent, onLogout }) => {
  const extractEmail = (raw) => {
    if (!raw) return '';
    const match = raw.match(/<(.+?)>/);
    return match ? match[1] : raw;
  };

  const [to, setTo] = useState(replyTo ? extractEmail(replyTo.from) : '');
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [subject, setSubject] = useState(replyTo ? `Re: ${(replyTo.subject || '').replace(/^Re:\s*/i, '')}` : '');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [files, setFiles] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [showVideoForm, setShowVideoForm] = useState(false);
  const [videoForm, setVideoForm] = useState({ name: '', email: '', date: '', time: '' });
  const [generatingLink, setGeneratingLink] = useState(false);

  const addFiles = (newFiles) => {
    Array.from(newFiles).forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(',')[1];
        setFiles((prev) => [...prev, { filename: file.name, content: base64, size: file.size }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeFile = (index) => setFiles((prev) => prev.filter((_, i) => i !== index));

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer.files.length) addFiles(e.dataTransfer.files);
  };

  const handleSend = async () => {
    if (!to || !subject || !body) return;
    setSending(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/admin/email/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          to,
          cc: cc || undefined,
          bcc: bcc || undefined,
          subject,
          html: `<div style="font-family:Arial,sans-serif;">${body.replace(/\n/g, '<br>')}</div>`,
          from_name: 'Philipp Nuraj',
          in_reply_to: replyTo?.message_id || null,
          attachments: files.length ? files.map(f => ({ filename: f.filename, content: f.content })) : null,
        }),
      });
      if (res.status === 401) { onLogout(); return; }
      const data = await res.json();
      if (data.status === 'ok') { onSent(); } else { setError(data.detail || 'Senden fehlgeschlagen'); }
    } catch (e) {
      setError(e.message);
    } finally {
      setSending(false);
    }
  };

  const inputCls = "w-full px-3 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder-gray-600 focus:border-emerald-500/50 focus:outline-none transition-colors";

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-5">
        <h3 className="text-white font-semibold text-sm">{replyTo ? 'Antworten' : 'Neue E-Mail verfassen'}</h3>
        <button onClick={onClose} className="text-gray-500 hover:text-white transition-colors"><X className="w-5 h-5" /></button>
      </div>

      <div className="space-y-3 mb-5">
        <div>
          <label className="text-xs text-gray-500 mb-1 block">Absender</label>
          <div className="px-3 py-2.5 rounded-lg bg-white/5 border border-white/10 text-emerald-400 text-sm">
            Philipp Nuraj &lt;philipp.nuraj@laizyones.de&gt;
          </div>
        </div>
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-xs text-gray-500">An</label>
            {!showCcBcc && (
              <button
                data-testid="show-cc-bcc"
                onClick={() => setShowCcBcc(true)}
                className="text-xs text-emerald-400 hover:text-emerald-300 transition-colors"
              >
                CC / BCC
              </button>
            )}
          </div>
          <input data-testid="compose-to" value={to} onChange={(e) => setTo(e.target.value)} className={inputCls} placeholder="empfaenger@example.com" />
        </div>
        {showCcBcc && (
          <>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">CC</label>
              <input data-testid="compose-cc" value={cc} onChange={(e) => setCc(e.target.value)} className={inputCls} placeholder="cc@example.com (mehrere mit Komma trennen)" />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">BCC</label>
              <input data-testid="compose-bcc" value={bcc} onChange={(e) => setBcc(e.target.value)} className={inputCls} placeholder="bcc@example.com (mehrere mit Komma trennen)" />
            </div>
          </>
        )}
        <div>
          <label className="text-xs text-gray-500 mb-1 block">Betreff</label>
          <input data-testid="compose-subject" value={subject} onChange={(e) => setSubject(e.target.value)} className={inputCls} placeholder="Betreff eingeben" />
        </div>
        <div>
          <label className="text-xs text-gray-500 mb-1 block">Nachricht</label>
          <textarea
            data-testid="compose-body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={8}
            className={`${inputCls} resize-none`}
            placeholder="Ihre Nachricht..."
            autoFocus
          />
        </div>

        <div>
          <label className="text-xs text-gray-500 mb-1 block">Anhänge</label>
          <div
            data-testid="drop-zone"
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
            onClick={() => document.getElementById('file-input').click()}
            className={`rounded-xl border-2 border-dashed p-4 text-center cursor-pointer transition-colors ${
              dragging ? 'border-emerald-400 bg-emerald-500/10' : 'border-white/10 hover:border-white/20 bg-white/2'
            }`}
          >
            <input id="file-input" type="file" multiple className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
            <Paperclip className={`w-5 h-5 mx-auto mb-1.5 ${dragging ? 'text-emerald-400' : 'text-gray-600'}`} />
            <p className="text-gray-500 text-xs">{dragging ? 'Dateien hier ablegen' : 'Klicken oder Dateien hierher ziehen'}</p>
          </div>
          {files.length > 0 && (
            <div className="mt-2 space-y-1.5">
              {files.map((f, i) => (
                <div key={i} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 border border-white/5">
                  <span className="text-gray-500">{fileIcon(f.filename)}</span>
                  <span className="text-white text-xs truncate flex-1">{f.filename}</span>
                  <span className="text-gray-600 text-xs">{formatSize(f.size)}</span>
                  <button onClick={() => removeFile(i)} className="text-gray-600 hover:text-red-400 transition-colors"><X className="w-3.5 h-3.5" /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {error && <p className="text-red-400 text-xs mb-3">{error}</p>}

      {/* Video Link Quick-Button */}
      <div className="mb-4">
        <button
          data-testid="video-link-btn"
          type="button"
          onClick={() => { setShowVideoForm(!showVideoForm); setVideoForm({ name: to ? to.split('@')[0] : '', email: to, date: '', time: '' }); }}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-xs font-medium hover:bg-cyan-500/20 transition-colors"
        >
          <Video className="w-3.5 h-3.5" /> Videocall-Link erstellen & einfügen
        </button>
        {showVideoForm && (
          <div className="mt-3 p-4 rounded-xl border border-cyan-500/20 bg-cyan-500/5 space-y-3">
            <p className="text-cyan-300 text-xs font-medium">Cal.eu Videocall erstellen</p>
            <div className="grid grid-cols-2 gap-2">
              <input data-testid="video-name" value={videoForm.name} onChange={e => setVideoForm(f => ({ ...f, name: e.target.value }))} className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-xs placeholder-gray-600 focus:border-cyan-500/50 focus:outline-none" placeholder="Teilnehmer-Name" />
              <input data-testid="video-email" value={videoForm.email} onChange={e => setVideoForm(f => ({ ...f, email: e.target.value }))} className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-xs placeholder-gray-600 focus:border-cyan-500/50 focus:outline-none" placeholder="Teilnehmer-E-Mail" />
              <input data-testid="video-date" type="date" value={videoForm.date} onChange={e => setVideoForm(f => ({ ...f, date: e.target.value }))} className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-xs focus:border-cyan-500/50 focus:outline-none" />
              <input data-testid="video-time" type="time" value={videoForm.time} onChange={e => setVideoForm(f => ({ ...f, time: e.target.value }))} className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-xs focus:border-cyan-500/50 focus:outline-none" />
            </div>
            <button
              data-testid="generate-video-link"
              onClick={async () => {
                if (!videoForm.name || !videoForm.email || !videoForm.date || !videoForm.time) return;
                setGeneratingLink(true);
                try {
                  const res = await fetch(`${API}/api/admin/generate-video-link`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                    body: JSON.stringify({ attendee_name: videoForm.name, attendee_email: videoForm.email, date: videoForm.date, time: videoForm.time }),
                  });
                  const data = await res.json();
                  if (data.status === 'success' && data.video_link) {
                    const dateStr = new Date(videoForm.date + 'T' + videoForm.time).toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });
                    const linkText = `\n\nVideokonferenz-Link für ${dateStr} um ${videoForm.time} Uhr:\n${data.video_link}\n`;
                    setBody(prev => prev + linkText);
                    if (!subject) setSubject(`Videokonferenz-Link — ${dateStr}`);
                    setShowVideoForm(false);
                  } else {
                    setError(data.detail || 'Videocall-Link konnte nicht erstellt werden');
                  }
                } catch (e) {
                  setError('Fehler beim Erstellen des Links');
                }
                setGeneratingLink(false);
              }}
              disabled={generatingLink || !videoForm.name || !videoForm.email || !videoForm.date || !videoForm.time}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-cyan-500 hover:bg-cyan-400 disabled:opacity-40 text-white text-xs font-semibold transition-colors"
            >
              <Video className="w-3.5 h-3.5" /> {generatingLink ? 'Erstelle...' : 'Link erstellen & einfügen'}
            </button>
          </div>
        )}
      </div>

      <button
        data-testid="send-button"
        onClick={handleSend}
        disabled={sending || !to || !body}
        className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 text-white text-sm font-semibold transition-colors"
      >
        <Send className="w-4 h-4" />
        {sending ? 'Sende...' : 'Senden als philipp.nuraj@laizyones.de'}
        {files.length > 0 && <span className="text-emerald-200 text-xs">({files.length} Anhang{files.length > 1 ? 'e' : ''})</span>}
      </button>
    </div>
  );
};

/* ─── Signature Editor ─── */
const SignatureEditor = ({ token, onLogout }) => {
  const [sig, setSig] = useState({
    enabled: false, name: '', position: '', phone: '', email: '', website: '', address: '', linkedin: '',
    logo_base64: '', logo_content_type: '',
  });
  const [previewHtml, setPreviewHtml] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loadingState, setLoadingState] = useState(true);
  const [logoDragging, setLogoDragging] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/admin/signature`, { headers });
        if (res.status === 401) { onLogout(); return; }
        if (res.ok) setSig(await res.json());
      } catch {}
      setLoadingState(false);
    })();
  }, []);

  const fetchPreview = useCallback(async (data) => {
    try {
      const res = await fetch(`${API}/api/admin/signature/preview`, { headers });
      if (res.ok) {
        const d = await res.json();
        setPreviewHtml(d.html || '');
      }
    } catch {}
  }, [token]);

  useEffect(() => {
    if (!loadingState) fetchPreview(sig);
  }, [loadingState]);

  const update = (key, value) => setSig(prev => ({ ...prev, [key]: value }));

  const handleLogoFile = (file) => {
    if (!file || !file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(',')[1];
      setSig(prev => ({ ...prev, logo_base64: base64, logo_content_type: file.type }));
    };
    reader.readAsDataURL(file);
  };

  const handleLogoDrop = (e) => {
    e.preventDefault();
    setLogoDragging(false);
    if (e.dataTransfer.files.length) handleLogoFile(e.dataTransfer.files[0]);
  };

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      const res = await fetch(`${API}/api/admin/signature`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(sig),
      });
      if (res.ok) {
        setSaved(true);
        setTimeout(() => setSaved(false), 2500);
        // Refresh preview
        const prev = await fetch(`${API}/api/admin/signature/preview`, { headers });
        if (prev.ok) {
          const d = await prev.json();
          setPreviewHtml(d.html || '');
        }
      }
    } catch {}
    setSaving(false);
  };

  const inputCls = "w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder-gray-600 focus:border-emerald-500/50 focus:outline-none transition-colors";

  if (loadingState) return <div className="p-8 text-center text-gray-600 text-sm">Lade Signatur...</div>;

  return (
    <div className="p-6 max-w-4xl">
      {/* Toggle */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button
            data-testid="signature-toggle"
            onClick={() => update('enabled', !sig.enabled)}
            className={`relative w-11 h-6 rounded-full transition-colors ${sig.enabled ? 'bg-emerald-500' : 'bg-white/10'}`}
          >
            <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${sig.enabled ? 'translate-x-5' : ''}`} />
          </button>
          <span className="text-white text-sm font-medium">Signatur {sig.enabled ? 'aktiviert' : 'deaktiviert'}</span>
        </div>
        <button
          data-testid="signature-save"
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 text-white text-sm font-semibold transition-colors"
        >
          {saved ? <><Check className="w-4 h-4" /> Gespeichert</> : saving ? 'Speichern...' : 'Speichern'}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Editor */}
        <div className="space-y-4">
          <h3 className="text-white text-sm font-semibold mb-3">Signatur bearbeiten</h3>

          {/* Logo Upload */}
          <div>
            <label className="text-xs text-gray-500 mb-1.5 block">Logo</label>
            <div
              data-testid="logo-dropzone"
              onDragOver={(e) => { e.preventDefault(); setLogoDragging(true); }}
              onDragLeave={() => setLogoDragging(false)}
              onDrop={handleLogoDrop}
              onClick={() => document.getElementById('logo-input').click()}
              className={`relative rounded-xl border-2 border-dashed p-4 text-center cursor-pointer transition-all ${
                logoDragging ? 'border-emerald-400 bg-emerald-500/10' : 'border-white/10 hover:border-white/20 bg-white/2'
              }`}
            >
              <input id="logo-input" type="file" accept="image/*" className="hidden" onChange={(e) => { if (e.target.files[0]) handleLogoFile(e.target.files[0]); e.target.value = ''; }} />
              {sig.logo_base64 ? (
                <div className="flex items-center gap-3">
                  <img
                    src={`data:${sig.logo_content_type || 'image/png'};base64,${sig.logo_base64}`}
                    alt="Logo"
                    className="w-14 h-14 object-contain rounded-lg bg-white/5 p-1"
                  />
                  <div className="text-left flex-1">
                    <p className="text-white text-xs font-medium">Logo hochgeladen</p>
                    <p className="text-gray-600 text-xs">Klicken oder ziehen um zu ersetzen</p>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); setSig(prev => ({ ...prev, logo_base64: '', logo_content_type: '' })); }}
                    className="text-gray-600 hover:text-red-400 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <>
                  <Upload className={`w-6 h-6 mx-auto mb-1.5 ${logoDragging ? 'text-emerald-400' : 'text-gray-600'}`} />
                  <p className="text-gray-500 text-xs">Logo hierher ziehen oder klicken</p>
                  <p className="text-gray-700 text-[10px] mt-0.5">PNG, JPG, SVG empfohlen</p>
                </>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Name</label>
              <input data-testid="sig-name" value={sig.name} onChange={(e) => update('name', e.target.value)} className={inputCls} placeholder="Philipp Nuraj" />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Position</label>
              <input data-testid="sig-position" value={sig.position} onChange={(e) => update('position', e.target.value)} className={inputCls} placeholder="Geschäftsführer" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Telefon</label>
              <input data-testid="sig-phone" value={sig.phone} onChange={(e) => update('phone', e.target.value)} className={inputCls} placeholder="+49 123 456789" />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">E-Mail</label>
              <input data-testid="sig-email" value={sig.email} onChange={(e) => update('email', e.target.value)} className={inputCls} placeholder="philipp.nuraj@laizyones.de" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Website</label>
              <input data-testid="sig-website" value={sig.website} onChange={(e) => update('website', e.target.value)} className={inputCls} placeholder="www.laizyones.de" />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">LinkedIn</label>
              <input data-testid="sig-linkedin" value={sig.linkedin} onChange={(e) => update('linkedin', e.target.value)} className={inputCls} placeholder="linkedin.com/in/..." />
            </div>
          </div>

          <div>
            <label className="text-xs text-gray-500 mb-1 block">Adresse</label>
            <input data-testid="sig-address" value={sig.address} onChange={(e) => update('address', e.target.value)} className={inputCls} placeholder="Musterstraße 1, 39104 Magdeburg" />
          </div>
        </div>

        {/* Live Preview */}
        <div>
          <h3 className="text-white text-sm font-semibold mb-3">Vorschau</h3>
          <div className="rounded-xl border border-white/10 p-5" style={{ background: 'rgba(255,255,255,0.03)' }}>
            {sig.enabled ? (
              previewHtml ? (
                <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
              ) : (
                <div className="rounded-xl border border-white/5 p-4" style={{ background: 'rgba(255,255,255,0.02)' }}>
                  {/* Live client-side preview while editing (before save) */}
                  <table cellPadding="0" cellSpacing="0" style={{ fontFamily: 'Arial, Helvetica, sans-serif', marginTop: 16, borderTop: '2px solid #10B981', paddingTop: 14 }}>
                    <tbody>
                      <tr>
                        {sig.logo_base64 && (
                          <td style={{ paddingRight: 14, verticalAlign: 'top' }}>
                            <img src={`data:${sig.logo_content_type || 'image/png'};base64,${sig.logo_base64}`} alt="Logo" style={{ width: 60, height: 'auto', borderRadius: 8 }} />
                          </td>
                        )}
                        <td style={{ verticalAlign: 'top' }}>
                          <div style={{ lineHeight: 1.5 }}>
                            {sig.name && <strong style={{ fontSize: 14, color: '#fff', display: 'block' }}>{sig.name}</strong>}
                            {sig.position && <span style={{ fontSize: 12, color: '#9ca3af', display: 'block' }}>{sig.position}</span>}
                          </div>
                          <div style={{ marginTop: 6, fontSize: 12, color: '#9ca3af', lineHeight: 1.6 }}>
                            {[sig.phone && `Tel: ${sig.phone}`, sig.email, sig.website].filter(Boolean).join(' | ')}
                          </div>
                          {sig.address && <div style={{ marginTop: 4, fontSize: 12, color: '#9ca3af' }}>{sig.address}</div>}
                          {sig.linkedin && <div style={{ marginTop: 4, fontSize: 12 }}><span style={{ color: '#10B981' }}>LinkedIn</span></div>}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )
            ) : (
              <p className="text-gray-600 text-sm text-center py-6">Signatur ist deaktiviert</p>
            )}
          </div>
          <p className="text-gray-600 text-xs mt-3">Die Signatur wird automatisch an jede ausgehende E-Mail angehängt, wenn sie aktiviert ist.</p>
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
